var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { failure, success } from '@devexperts/remote-data-ts';
import { combineReader } from '@devexperts/utils/dist/adt/reader.utils';
import { ord } from 'fp-ts';
import { and, constTrue, not } from 'fp-ts/lib/function';
import { fromNullable } from 'fp-ts/lib/Option';
import { ordNumber } from 'fp-ts/lib/Ord';
import { asks } from 'fp-ts/lib/Reader';
import { contramap, setoidNumber, setoidString } from 'fp-ts/lib/Setoid';
import { array, string, type } from 'io-ts';
import { from, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { v4 as uuid } from 'uuid';
import { AxiosReader, dataUrlToBlob, readFiles, toRemoteData } from '../utils/axios';
import { createFormError } from '../utils/error.utils';
import { isDefined, mapRD, switchMapRD } from '../utils/object.utils';
import { getProductTeam } from './teams.service';
export var defaultStatuses = [
    { id: 1, order: 1, name: 'To Doooo', color: '#0042FF' },
    { id: 2, order: 2, name: 'In Progress', color: '#c1c1c1' },
    { id: 3, order: 3, name: 'Needs Review', color: '#FFAA00' },
    { id: 4, order: 4, name: 'Blocked', color: '#FF4E4A' },
    { id: 5, order: 5, name: 'Cancelled', color: '#7F8FA4' },
    { id: 6, order: 6, name: 'Completed', color: '#26C164' },
];
export var noteStatusOrd = function (statuses) {
    return ord.fromCompare(function (a, b) {
        return ordNumber.compare(statuses.findIndex(function (s) { return a === s.id; }), statuses.findIndex(function (s) { return b === s.id; }));
    });
};
export var noteByStatusOrd = function (statuses) {
    var statusOrd = noteStatusOrd(statuses);
    return ord.contramap(function (note) { return note.status_id; }, statusOrd);
};
export var priorityOrdering = ['none', 'low', 'medium', 'high', 'urgent'];
export var notePriorityOrd = ord.fromCompare(function (a, b) {
    return ordNumber.compare(priorityOrdering.indexOf(a), priorityOrdering.indexOf(b));
});
export var noteByPriorityOrd = ord.contramap(function (note) { return note.priority; }, notePriorityOrd);
var ProductService = /** @class */ (function () {
    function ProductService(axios) {
        this.axios = axios;
    }
    ProductService.prototype.getAll = function (withArchived) {
        if (withArchived === void 0) { withArchived = false; }
        var url = "/api/products";
        return toRemoteData(this.axios.get(url)).pipe(mapRD(function (data) { return (withArchived ? data : data.filter(not(isArchived))); }));
    };
    ProductService.prototype.getById = function (id) {
        var url = "/api/products/" + id;
        return toRemoteData(this.axios.get(url));
    };
    ProductService.prototype.getTeamProducts = function (teamId, withArchived) {
        if (withArchived === void 0) { withArchived = false; }
        var url = "/api/teams/" + teamId + "/products";
        return toRemoteData(this.axios.get(url)).pipe(mapRD(function (data) { return (withArchived ? data : data.filter(not(isArchived))); }));
    };
    ProductService.prototype.getNotes = function (productId, withEarliestComment) {
        if (withEarliestComment === void 0) { withEarliestComment = false; }
        var url = withEarliestComment
            ? "/api/products/" + productId + "/notes?comment=earliest"
            : "/api/products/" + productId + "/notes";
        return toRemoteData(this.axios.get(url)).pipe(mapRD(function (notes) { return notes.map(parseProductImage); }));
    };
    ProductService.prototype.moveProduct = function (productId, newTeamId) {
        return toRemoteData(this.axios.post("/api/products/" + productId + "/move", {
            new_team_id: newTeamId,
        }));
    };
    ProductService.prototype.transferProduct = function (productId, newOwner) {
        return toRemoteData(this.axios.post("/api/products/" + productId + "/transfer", {
            new_owner_email: newOwner,
        }));
    };
    ProductService.prototype.deleteProduct = function (id) {
        return toRemoteData(this.axios.delete("/api/products/" + id));
    };
    ProductService.prototype.setArchiveProduct = function (id, archive) {
        return toRemoteData(this.axios.patch("/api/products/" + id + "/archive", { archive: archive }));
    };
    ProductService.prototype.removeCollaborator = function (projectId, userId) {
        return toRemoteData(this.axios.delete("api/products/" + projectId + "/collaborators/" + userId));
    };
    ProductService.prototype.removeProspect = function (projectId, prospectId) {
        return toRemoteData(this.axios.delete("api/products/" + projectId + "/prospects/" + prospectId));
    };
    ProductService.prototype.resendProspectInvite = function (projectId, prospectId) {
        return toRemoteData(this.axios.post("api/products/" + projectId + "/reshare", { prospect_id: prospectId }));
    };
    ProductService.prototype.createProduct = function (teamId, name) {
        var url = teamId.fold('/api/products', function (id) { return "/api/teams/" + id + "/products"; });
        return toRemoteData(this.axios.post(url, { name: name }));
    };
    ProductService.prototype.createProductFromUploadedImages = function (teamId, name, files) {
        var url = teamId.fold('/api/products', function (id) { return "/api/teams/" + id + "/products"; });
        var data = new FormData();
        data.append('name', name);
        files.forEach(function (file) { return data.append('upload_images', dataUrlToBlob(file.data), file.name); });
        return toRemoteData(this.axios.post(url, data, {
            headers: { 'Content-Type': 'multipart/form-data' },
        }));
    };
    ProductService.prototype.addMoreUploadedImages = function (productId, files) {
        var url = "api/products/" + productId + "/upload-image";
        var data = new FormData();
        files.forEach(function (file) { return data.append('upload_images', dataUrlToBlob(file.data), file.name); });
        return toRemoteData(this.axios.post(url, data, {
            headers: { 'Content-Type': 'multipart/form-data' },
        }));
    };
    ProductService.prototype.getComments = function (noteId) {
        return toRemoteData(this.axios.get("/api/notes/" + noteId + "/comments"));
    };
    ProductService.prototype.getProductUploadedImages = function (projectId) {
        return toRemoteData(this.axios.get("/api/products/" + projectId + "/upload-image-notes")).pipe(mapRD(function (images) {
            return images.map(function (image) { return (__assign(__assign({}, image), { notes: image.notes.map(function (note) { return parseProductImage(note); }) })); });
        }));
    };
    ProductService.prototype.postWidgetImage = function (productId, widgetId, data) {
        return this.doPostProductImage("/api/widgets/public/" + widgetId + "/notes", __assign(__assign({}, data), { productId: productId }), true);
    };
    ProductService.prototype.postProductImage = function (data) {
        return this.doPostProductImage("/api/notes", __assign(__assign({}, data), { externalAuthor: undefined }));
    };
    ProductService.prototype.doPostProductImage = function (action, data, withCredentials) {
        if (withCredentials === void 0) { withCredentials = false; }
        var imageData = new FormData();
        var fileNameBase = "p" + data.productId + "-" + uuid();
        if (data.imageDataUrl) {
            imageData.append('image', dataUrlToBlob(data.imageDataUrl), fileNameBase + ".png");
        }
        imageData.append('product_id', "" + data.productId);
        if (data.product_upload_image_id) {
            imageData.append('product_upload_image_id', "" + data.product_upload_image_id);
        }
        imageData.append('source', data.source);
        imageData.append('x', "" + data.x);
        imageData.append('y', "" + data.y);
        imageData.append('pixel_ratio', "" + data.pixel_ratio);
        imageData.append('text', "" + data.comment);
        imageData.append('browser', "" + data.browser);
        imageData.append('os', "" + data.os);
        if (data.externalAuthor) {
            imageData.append('external_author', data.externalAuthor);
        }
        data.mentions.forEach(function (mention, i) {
            imageData.append("mentions[" + i + "]", "" + mention);
        });
        if (data.attachments) {
            data.attachments.forEach(function (att) {
                imageData.append('attachments', dataUrlToBlob(att.data), att.name);
            });
        }
        if (data.overlayDataUrl) {
            imageData.append('overlay', dataUrlToBlob(data.overlayDataUrl), fileNameBase + "-overlay.png");
            imageData.append('overlay_details', data.overlayJson || 'null');
        }
        if (data.video) {
            imageData.append('video_path', data.video.videoUrl);
            imageData.append('video_duration', String(Math.ceil(data.video.duration / 1000)));
            imageData.append('video_size', String(data.video.size));
            if (data.video.thumbnailDataUrl) {
                imageData.append('video_thumbnail', dataUrlToBlob(data.video.thumbnailDataUrl), fileNameBase + "-thumb.png");
            }
        }
        return toRemoteData(this.axios.post(action, imageData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            withCredentials: withCredentials,
        }));
    };
    ProductService.prototype.setStatus = function (noteId, status_id) {
        return toRemoteData(this.axios.patch("api/notes/" + noteId, { status_id: status_id }));
    };
    ProductService.prototype.setPriority = function (noteId, priority) {
        return toRemoteData(this.axios.patch("api/notes/" + noteId, { priority: formatNotePriorityToApi(priority) }));
    };
    ProductService.prototype.setAssignee = function (noteId, userId) {
        return toRemoteData(this.axios.patch("api/notes/" + noteId, {
            assignee_id: typeof userId === 'number' ? userId : -1,
        })).pipe(mapRD(parseProductImage));
    };
    ProductService.prototype.deleteNote = function (noteId) {
        return toRemoteData(this.axios.delete("api/notes/" + noteId));
    };
    ProductService.prototype.share = function (productId, emails) {
        return toRemoteData(this.axios.post("api/products/" + productId + "/share", { emails: emails }));
    };
    ProductService.prototype.createWidget = function (productId, data) {
        var _this = this;
        var thumbnail$ = data.logo ? from(readFiles([data.logo])) : of([]);
        return thumbnail$.pipe(switchMap(function (files) {
            var form = new FormData();
            form.append('product_id', String(productId));
            form.append('text_color', data.text_color);
            form.append('background_color', data.background_color);
            form.append('button_text', data.button_text);
            form.append('position', data.position);
            form.append('powered_by_volley', String(data.powered_by_volley));
            form.append('only_members', String(data.only_members));
            if (files.length) {
                form.append('logo', dataUrlToBlob(files[0].data), files[0].name);
            }
            return toRemoteData(_this.axios.post("api/widgets", form, {
                headers: { 'Content-Type': 'multipart/form-data' },
            }));
        }));
    };
    ProductService.prototype.updateWidget = function (widgetId, data) {
        var _this = this;
        var thumbnail$ = data.logo ? from(readFiles([data.logo])) : of([]);
        return thumbnail$.pipe(switchMap(function (files) {
            var form = new FormData();
            form.append('text_color', data.text_color);
            form.append('background_color', data.background_color);
            form.append('button_text', data.button_text);
            form.append('position', data.position);
            form.append('powered_by_volley', String(data.powered_by_volley));
            form.append('only_members', String(data.only_members));
            if (files.length) {
                form.append('logo', dataUrlToBlob(files[0].data), files[0].name);
            }
            return toRemoteData(_this.axios.patch("api/widgets/" + widgetId, form, {
                headers: { 'Content-Type': 'multipart/form-data' },
            }));
        }));
    };
    ProductService.prototype.updateWorkflow = function (productId, workflow) {
        var statuses = workflow.map(function (s, i) { return ({
            id: s.id > 0 ? s.id : undefined,
            product_id: productId,
            order: i + 1,
            name: s.name,
            color: s.color,
            is_deleted: !!s.is_deleted,
        }); });
        // statuses.sort((a, b) => (a.id ?? 0) - (b.id ?? 0));
        return toRemoteData(this.axios.post("api/edit-statuses", { statuses: statuses }));
    };
    ProductService.prototype.checkCanDeleteStatus = function (statusId) {
        return toRemoteData(this.axios.post("api/delete-status-check", { status_id: statusId }));
    };
    ProductService.prototype.update = function (productId, data) {
        var _this = this;
        var name = data.name, thumbnail = data.thumbnail;
        var thumbnail$ = thumbnail ? from(readFiles([thumbnail])) : of([]);
        return thumbnail$.pipe(switchMap(function (files) {
            var body = new FormData();
            body.append('name', name);
            if (files.length) {
                body.append("thumbnail", dataUrlToBlob(files[0].data), files[0].name);
            }
            return toRemoteData(_this.axios.patch("api/products/" + productId, body, {
                headers: { 'Content-Type': 'multipart/form-data' },
            }));
        }), switchMapRD(function () { return _this.getAll(true); }), map(function (result) {
            return result.chain(function (products) {
                var product = products.find(function (p) { return p.id === productId; });
                return product
                    ? success(product)
                    : failure(new Error('not found'));
            });
        }));
    };
    return ProductService;
}());
export { ProductService };
var ProductServiceFactory = function (axios) { return new ProductService(axios); };
export var ProductServiceReader = asks(function (ctx) { return new ProductService(ctx.axios); });
export var ProductsService = combineReader(AxiosReader, ProductServiceFactory);
export function parseProductImage(_a) {
    var status = _a.status, raw = __rest(_a, ["status"]);
    return __assign(__assign({}, raw), { pixel_ratio: fromNullable(raw.pixel_ratio).filter(function (x) { return x !== 0; }), status_id: status.id, priority: raw.priority ? parseNotePriorityFromApi(raw.priority) : 'none' });
}
function formatNotePriorityToApi(priority) {
    return priority;
}
function parseNotePriorityFromApi(priority) {
    return priority;
}
export function getMembers(product) {
    var teamMembers = product.team ? product.team.members : [];
    return __spreadArray(__spreadArray([product.owner], teamMembers, true), product.collaborators, true).filter(isDefined);
}
export function getMembersOrEmpty(product) {
    return product.map(getMembers).getOrElse([]);
}
export function isArchived(product) {
    return !!product.archived_at;
}
export function pluralProjects(num) {
    switch (num) {
        case 1:
            return 'project';
        default:
            return 'projects';
    }
}
export function filterLimitedProjects(products, userId) {
    return products.filter(function (p) { return !isArchived(p) && getProductTeam(p).isNone() && p.owner && p.owner.id === userId; });
}
export function countLimitedProjects(products, userId) {
    return filterLimitedProjects(products, userId).length;
}
var allErrors = type({
    __all__: array(string),
});
export function parseProductCreationError(e) {
    if (e.response) {
        var _a = e.response, status_1 = _a.status, data = _a.data;
        if (status_1 === 400) {
            return allErrors.decode(data).fold(function () { return e; }, function (data) { return createFormError(data.__all__.join('\n')); });
        }
    }
    return e;
}
export var productIdSetoid = contramap(function (p) { return p.id; }, setoidNumber);
export var productIdAndDateSetoid = contramap(function (p) {
    var date = p.updated_at || p.created_at;
    var prospects = p.prospects
        .map(function (p) { return p.id; })
        .sort()
        .join('-');
    var widget = JSON.stringify(p.widget);
    var statuses = JSON.stringify(p.statuses);
    return p.id + "-" + date + "-" + prospects + "-" + widget + "-" + statuses;
}, setoidString);
export var defaultNoteFilter = { assignee: [], status: [], priority: [] };
export var filterNotesByStatuses = function (statuses, filter, showResolved) {
    var _a;
    var finalStatus = (_a = statuses[statuses.length - 1]) === null || _a === void 0 ? void 0 : _a.id;
    var effectiveStatuses = showResolved && finalStatus ? __spreadArray(__spreadArray([], filter.status, true), [finalStatus], false) : filter.status;
    var statusPredicate = filter.status.length > 0
        ? function (n) { return effectiveStatuses.includes(n.status_id); }
        : showResolved
            ? constTrue
            : function (n) { return n.status_id !== finalStatus; };
    var priorityPredicate = filter.priority.length > 0 ? function (n) { return filter.priority.includes(n.priority); } : constTrue;
    var assigneePredicate = filter.assignee.length > 0
        ? function (n) { return filter.assignee.includes((n.assignee ? n.assignee.id : null) || null); }
        : constTrue;
    var predicate = [statusPredicate, priorityPredicate, assigneePredicate].reduce(and, constTrue);
    return function (notes) { return notes.filter(predicate); };
};
export var extractPageFromUrl = function (url) {
    if (!url)
        return 'Unknown';
    try {
        var parsed = new URL(url);
        return parsed.host + parsed.pathname;
    }
    catch (e) {
        return url.replace(/^https?:\/\//i, '').replace(/[?#].*$/, '');
    }
};
//# sourceMappingURL=products.service.js.map